import { flushSync } from 'react-dom';
import { useStore } from '../../store';
import { composerInputId } from './useConversation';

/** The chat surfaces: a key or focus inside one of them belongs to chat. */
const CHAT_SURFACE =
  '[data-chat-dock],[data-chat-window],[data-chat-tab],[data-chat-sheet]';

const find = (selector: string) =>
  document.querySelector<HTMLElement>(selector);

/**
 * Window ids a reload restored. Opening a window focuses its composer, but a
 * restore is not an open, so the composer skips these once.
 */
export const restoredWindowIds = new Set<string>();

export const isInChat = (el: Element | null): boolean =>
  !!el?.closest(CHAT_SURFACE);

export const isTextEntry = (el: Element | null): boolean =>
  el instanceof HTMLInputElement ||
  el instanceof HTMLTextAreaElement ||
  (el instanceof HTMLElement && el.isContentEditable);

/**
 * Run a store change and focus an element the change renders. `flushSync`
 * commits the change first, so the target is in the DOM when it is looked up.
 */
function thenFocus(change: () => void, selector: string): void {
  flushSync(change);
  find(selector)?.focus();
}

export const focusDockSearch = () => find('[data-chat-search]')?.focus();

export function openDockAndFocusSearch(open: () => void): void {
  thenFocus(open, '[data-chat-search]');
}

export function collapseDockAndFocusBar(): void {
  thenFocus(
    () => useStore.getState().setChatDockOpen(false),
    '[data-chat-dock-bar]'
  );
}

export function minimizeAndFocusTab(id: string): void {
  thenFocus(
    () => useStore.getState().minimizeChatWindow(id),
    `[data-chat-tab="${id}"]`
  );
}

/** The dock row for the conversation, else the bar, else the dock's search. */
export function closeAndFocusDock(id: string): void {
  (
    find(`[data-chat-row="${id}"]`) ??
    find('[data-chat-dock-bar]') ??
    find('[data-chat-search]')
  )?.focus();
  useStore.getState().closeChatWindow(id);
}

/** A dock row press: a window that is already open gets its composer back. */
export function openChatAndFocus(id: string): void {
  flushSync(() => useStore.getState().openChatWindow(id));
  document.getElementById(composerInputId(id))?.focus();
}
